import { todayIsoLocal } from './portalDateFormat'

export const DEFAULT_MINUTOS_ANTICIPACION_TURNOS = 60

export function normalizeMinutosAnticipacionTurnos(
  value,
  fallback = DEFAULT_MINUTOS_ANTICIPACION_TURNOS,
) {
  const raw = value == null || value === '' ? fallback : value
  const n = Number(raw)
  if (!Number.isFinite(n)) return DEFAULT_MINUTOS_ANTICIPACION_TURNOS
  return Math.max(0, Math.trunc(n))
}

function truncateToMinute(dt) {
  const out = new Date(dt)
  out.setSeconds(0, 0)
  return out
}

export function cumpleMinutosAnticipacionTurno(
  fechaHoraTurno,
  minutosAnticipacionTurnos,
  ahora = new Date(),
) {
  if (!(fechaHoraTurno instanceof Date) || Number.isNaN(fechaHoraTurno.getTime())) {
    return false
  }
  const minutos = normalizeMinutosAnticipacionTurnos(minutosAnticipacionTurnos)
  const limite = truncateToMinute(ahora)
  limite.setMinutes(limite.getMinutes() + minutos)
  return minutos > 0
    ? fechaHoraTurno.getTime() >= limite.getTime()
    : fechaHoraTurno.getTime() > limite.getTime()
}

/** Rango ISO `[YYYY-MM-DD]` para el calendario mensual del paso 3.
 *  - Mes en curso: desde hoy hasta fin del mes.
 *  - Otro mes: desde día 1 hasta fin del mes (00 día siguiente del próximo mes). */
export function paso3RangoIsoDelMes(anio, mes) {
  const y = Number(anio)
  const m = Number(mes)
  if (!Number.isFinite(y) || !Number.isFinite(m)) return { fechaDesde: '', fechaHasta: '' }
  const hoyIso = todayIsoLocal()
  const [hy, hm] = hoyIso.split('-').map(Number)
  const esMesActual = y === hy && m + 1 === hm
  const ultimoDia = new Date(y, m + 1, 0).getDate()
  const pad = (n) => String(n).padStart(2, '0')
  const fechaHasta = `${y}-${pad(m + 1)}-${pad(ultimoDia)}`
  const fechaDesde = esMesActual ? hoyIso : `${y}-${pad(m + 1)}-01`
  return { fechaDesde, fechaHasta }
}

export function paso3CacheKey(ubicId, anio, mes) {
  const u = Number(ubicId)
  const y = Number(anio)
  const m = Number(mes)
  if (!Number.isFinite(u) || !Number.isFinite(y) || !Number.isFinite(m)) return ''
  return `${u}|${y}-${String(m + 1).padStart(2, '0')}`
}

export function extractTurnosDisponiblesList(data) {
  if (data == null) return []
  if (Array.isArray(data)) return data
  const inner = data.data
  if (Array.isArray(inner)) return inner
  return []
}

function horaIntToHM(n) {
  if (n == null || n === '') return null
  if (typeof n === 'string') {
    const t = String(n).trim()
    const mTime = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(t)
    if (mTime) {
      const hh = Number(mTime[1])
      const mm = Number(mTime[2])
      if (hh >= 0 && hh < 24 && mm >= 0 && mm < 60) return { h: hh, m: mm }
      return null
    }
    const v = Number(t.replace(',', '.'))
    return Number.isFinite(v) ? horaIntToHM(v) : null
  }
  const v = Number(n)
  if (!Number.isFinite(v)) return null

  if (v >= 100 && v <= 2359) {
    const h = Math.floor(v / 100)
    const m = v % 100
    if (h < 24 && m < 60) return { h, m }
  }

  if (Number.isInteger(v) && v >= 0 && v < 24) {
    return { h: v, m: 0 }
  }

  if (v >= 0 && v <= 24 * 60) {
    return { h: Math.floor(v / 60), m: v % 60 }
  }

  const h = Math.floor(v / 100)
  const m = v % 100
  if (m < 60 && h < 24) return { h, m }
  return null
}

function parseFechaTurnoToLocalDate(fechaStr) {
  const s = String(fechaStr ?? '').trim()
  if (!s) return null
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})(?=[Tt\s]|$)/.exec(s)
  if (isoDate) {
    const y = Number(isoDate[1])
    const mo = Number(isoDate[2])
    const d = Number(isoDate[3])
    return new Date(y, mo - 1, d)
  }
  const dm = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s)
  if (dm) {
    return new Date(Number(dm[3]), Number(dm[2]) - 1, Number(dm[1]))
  }
  const dt = new Date(s)
  if (Number.isNaN(dt.getTime())) return null
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
}

function combineFechaHora(fechaStr, horaInt) {
  const base = parseFechaTurnoToLocalDate(fechaStr)
  if (!base) return null
  const hm = horaIntToHM(horaInt)
  if (!hm) return null
  return new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate(),
    hm.h,
    hm.m,
    0,
    0,
  )
}

function readPositiveNumber(...values) {
  for (const value of values) {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

function minutesFromHoraLike(hora) {
  const hm = horaIntToHM(hora)
  if (!hm) return null
  return hm.h * 60 + hm.m
}

function readTurnoTiempo(cro, bloque) {
  const explicit = readPositiveNumber(
    cro?.turnoTiempo,
    cro?.turtTiempo,
    cro?.tiempo,
    cro?.duracion,
    bloque?.turnoTiempo,
    bloque?.turtTiempo,
    bloque?.tiempo,
    bloque?.duracion,
  )
  if (explicit) return explicit

  const hsIniMin = minutesFromHoraLike(cro?.hora ?? bloque?.hora)
  const hsFinMin = minutesFromHoraLike(cro?.hsFin ?? cro?.horaFin ?? bloque?.hsFin ?? bloque?.horaFin)
  if (hsIniMin == null || hsFinMin == null || hsFinMin <= hsIniMin) return null
  return hsFinMin - hsIniMin
}

function readPrestadorNombreFromCro(cro) {
  return String(
    cro?.nombre ??
      cro?.nombrePrestador ??
      cro?.preNombre ??
      cro?.prestadorNombre ??
      '',
  ).trim()
}

function readBoolFlag(...values) {
  for (const value of values) {
    if (value === true || value === 1 || value === '1' || value === 'true') return true
    if (value === false || value === 0 || value === '0' || value === 'false') return false
  }
  return false
}

/** Un cronograma con videoconsulta puede tomarse presencial si `permiteTurnoPresencial`. */
function modalidadesEfectivasDelCronograma(esVideoCronograma, permiteTurnoPresencial, modalidad) {
  const canVideo = esVideoCronograma
  const canPresencial = !esVideoCronograma || permiteTurnoPresencial
  const out = []
  if ((modalidad === 'presencial' || modalidad === 'ambos') && canPresencial) {
    out.push(false)
  }
  if ((modalidad === 'video' || modalidad === 'ambos') && canVideo) {
    out.push(true)
  }
  return out
}

function buildSlotKey(fechaRaw, horaNum, esVideo, cro) {
  const g = String(cro?.guid ?? '').trim()
  // Incluye modalidad efectiva: un mismo guid híbrido puede emitirse como presencial y video.
  if (g) return `g:${g}|m:${esVideo ? 1 : 0}`
  const croId = cro?.id ?? ''
  const preId = cro?.preId ?? ''
  return `k:${String(fechaRaw).trim()}|${horaNum}|${esVideo ? 1 : 0}|${croId}|${preId}`
}

export function collectAsistenteSlotsFromDias(dias, options = {}) {
  const modalidad = options.modalidad ?? 'ambos'
  const minutosAnticipacionTurnos = normalizeMinutosAnticipacionTurnos(
    options.minutosAnticipacionTurnos,
  )
  const list = extractTurnosDisponiblesList(dias)
  const out = []
  if (!Array.isArray(list)) return out
  for (const day of list) {
    const fechaRaw = day.fecha ?? ''
    const horarios = day.horarios ?? []
    if (!Array.isArray(horarios)) continue
    for (const bloque of horarios) {
      const croList = bloque.cronogramas ?? []
      const horaBloque = bloque.hora
      if (!Array.isArray(croList)) continue
      for (const cro of croList) {
        if (cro?.turnoSuperpuesto || bloque?.turnoSuperpuesto) continue
        const hora = cro.hora ?? horaBloque
        const esVideoCronograma = readBoolFlag(
          cro.esVideoConsulta,
          cro.esVideoconsulta,
          bloque?.esVideoConsulta,
          bloque?.esVideoconsulta,
        )
        const permiteTurnoPresencial = readBoolFlag(
          cro.permiteTurnoPresencial,
          cro.PermiteTurnoPresencial,
          bloque?.permiteTurnoPresencial,
          bloque?.PermiteTurnoPresencial,
        )
        const modalidadesEfectivas = modalidadesEfectivasDelCronograma(
          esVideoCronograma,
          permiteTurnoPresencial,
          modalidad,
        )
        if (modalidadesEfectivas.length === 0) continue
        const dt = combineFechaHora(fechaRaw, hora)
        if (!dt || Number.isNaN(dt.getTime())) continue
        if (!cumpleMinutosAnticipacionTurno(dt, minutosAnticipacionTurnos)) continue
        const prestadorNombre = readPrestadorNombreFromCro(cro)
        const horaNum = Number(hora)
        const fechaNorm = String(fechaRaw).trim()
        const isoDia = toIsoDateLocal(dt)
        const hsFin = cro?.hsFin ?? cro?.horaFin ?? bloque?.hsFin ?? bloque?.horaFin ?? null
        const turtId =
          cro?.turtId ??
          cro?.turtTiempoId ??
          bloque?.turtId ??
          bloque?.turtTiempoId ??
          null
        const turtTiempo = readTurnoTiempo(cro, bloque)
        const croId = cro?.id ?? null
        const preId = cro?.preId ?? null
        const consId = cro?.consId ?? null
        const guid = String(cro?.guid ?? '').trim() || null
        for (const esVideo of modalidadesEfectivas) {
          const bucketKey = `${isoDia}|${horaNum}|${esVideo ? 1 : 0}`
          out.push({
            dt,
            esVideo,
            permiteTurnoPresencial,
            fechaRaw: fechaNorm,
            hora: horaNum,
            hsFin,
            turtId,
            turtTiempo,
            prestadorNombre,
            croId,
            preId,
            consId,
            guid,
            bucketKey,
            slotKey: buildSlotKey(fechaNorm, horaNum, esVideo, cro),
          })
        }
      }
    }
  }
  out.sort((a, b) => a.dt - b.dt)
  return out
}

export function dayAvailabilityMarkersByIsoFromDias(dias, options = {}) {
  const slots = collectAsistenteSlotsFromDias(dias, {
    modalidad: 'ambos',
    minutosAnticipacionTurnos: options.minutosAnticipacionTurnos,
  })
  /** @type {Map<string, { presencial: boolean, video: boolean }>} */
  const map = new Map()
  for (const s of slots) {
    const iso = toIsoDateLocal(s.dt)
    if (!iso) continue
    let m = map.get(iso)
    if (!m) {
      m = { presencial: false, video: false }
      map.set(iso, m)
    }
    if (s.esVideo) m.video = true
    else m.presencial = true
  }
  return map
}

function toIsoDateLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

export { toIsoDateLocal }

/** Etiqueta `HH:mm hs` desde un `Date` local. */
export function formatHoraLabelHs(dt) {
  if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) return ''
  const h = String(dt.getHours()).padStart(2, '0')
  const mi = String(dt.getMinutes()).padStart(2, '0')
  return `${h}:${mi} hs`
}

function combineFechaHoraDesdePrimerTurno(primer) {
  if (!primer) return null
  return combineFechaHora(primer.fecha, primer.hora)
}

function capitalizarPrimera(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : ''
}

function formatPrimerTurnoTextoLargo(primer) {
  const dt = combineFechaHoraDesdePrimerTurno(primer)
  if (!dt || Number.isNaN(dt.getTime())) return ''
  const fechaFmt = dt
    .toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace(',', '')
  const h = String(dt.getHours()).padStart(2, '0')
  const mi = String(dt.getMinutes()).padStart(2, '0')
  return `${capitalizarPrimera(fechaFmt)} ${h}:${mi} hs`
}

function formatPrimerTurnoTextoCorto(primer) {
  const dt = combineFechaHoraDesdePrimerTurno(primer)
  if (!dt || Number.isNaN(dt.getTime())) return ''
  const weekday = dt.toLocaleDateString('es-AR', { weekday: 'long' })
  const dd = String(dt.getDate()).padStart(2, '0')
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const yyyy = dt.getFullYear()
  const h = String(dt.getHours()).padStart(2, '0')
  const mi = String(dt.getMinutes()).padStart(2, '0')
  return `${capitalizarPrimera(weekday)} ${dd}/${mm}/${yyyy} ${h}:${mi}`
}

export function mapearSedeBase({ id, nombre } = {}) {
  return {
    id: Number(id),
    nombre: String(nombre ?? '').trim(),
    primerPresencial: null,
    primerVideoconsulta: null,
    hayPresencial: false,
    hayVideo: false,
    textoPresencial: '',
    textoVideo: '',
    textoPresencialCorto: '',
    textoVideoCorto: '',
    prestadorPresencial: '',
    prestadorVideo: '',
  }
}

/** Mapea cada item de POST `Turnos/primeros-turnos-por-ubicacion` al shape consumido por Step2/Step3. */
export function mapearSedePrimerosTurnos(item) {
  const id = Number(item?.ubicId)
  const nombre = String(item?.ubicacion ?? '').trim()
  const primerPresencial = item?.primerPresencial ?? null
  const primerVideoconsulta = item?.primerVideoconsulta ?? null
  const hayPresencial = !!primerPresencial
  const hayVideo = !!primerVideoconsulta
  return {
    id,
    nombre,
    primerPresencial,
    primerVideoconsulta,
    hayPresencial,
    hayVideo,
    textoPresencial: hayPresencial ? formatPrimerTurnoTextoLargo(primerPresencial) : '',
    textoVideo: hayVideo ? formatPrimerTurnoTextoLargo(primerVideoconsulta) : '',
    textoPresencialCorto: hayPresencial ? formatPrimerTurnoTextoCorto(primerPresencial) : '',
    textoVideoCorto: hayVideo ? formatPrimerTurnoTextoCorto(primerVideoconsulta) : '',
    prestadorPresencial: hayPresencial ? String(primerPresencial.prestador ?? '').trim() : '',
    prestadorVideo: hayVideo ? String(primerVideoconsulta.prestador ?? '').trim() : '',
  }
}

/** Algunos endpoints devuelven `[…]` y otros `{ data: […] }`. Normaliza a array. */
export function extractTaxonomyItemsList(data) {
  if (data == null) return []
  if (Array.isArray(data)) return data
  const inner = data.data
  if (Array.isArray(inner)) return inner
  return []
}

/** Slot del paso 3 a partir del primer turno (presencial/video) que devuelve el paso 2. */
export function buildEarliestPaso3SlotFromPrimerTurno(sede, esVideo) {
  if (!sede) return null
  const primer = esVideo ? sede.primerVideoconsulta : sede.primerPresencial
  if (!primer) return null
  const dt = combineFechaHoraDesdePrimerTurno(primer)
  if (!dt || Number.isNaN(dt.getTime())) return null
  const fechaIso = toIsoDateLocal(dt)
  const horaNum = Number(primer.hora)
  const bucketKey = `${fechaIso}|${horaNum}|${esVideo ? 1 : 0}`
  const guid = String(primer.guid ?? '').trim()
  const slotKey = guid
    ? `g:${guid}|m:${esVideo ? 1 : 0}`
    : `k:${fechaIso}|${horaNum}|${esVideo ? 1 : 0}|${primer.croId ?? ''}|${primer.preId ?? ''}`
  return {
    slotKey,
    bucketKey,
    fechaIso,
    horaLabel: formatHoraLabelHs(dt),
    esVideo,
    permiteTurnoPresencial: readBoolFlag(
      primer.permiteTurnoPresencial,
      primer.PermiteTurnoPresencial,
    ),
    turtId: primer.turtId ?? null,
    turtTiempo: readPositiveNumber(primer.turnoTiempo, primer.turtTiempo, primer.tiempo),
    hsFin: primer.hsFin ?? primer.horaFin ?? null,
    prestadorNombre: String(primer.prestador ?? '').trim(),
    croId: primer.croId ?? null,
    preId: primer.preId ?? null,
    consId: primer.consId ?? null,
    guid: guid || null,
    ubicId: sede.id,
    modalidad: esVideo ? 'video' : 'presencial',
  }
}

